/**
 * Gamepads with the standard mapping (GDD §8): the left stick or D-pad steers, the stick's up and down set the
 * throttle, A or the right trigger fires, Start pauses (and starts a run from the title). In menus the D-pad moves
 * the focus, A presses and B goes back. Pads are polled once a frame, only while one is connected.
 */
import { createSource, releaseSource, type CommandSink, type ControlSource } from './actions.ts';

/** Standard-mapping button indices (W3C Gamepad, "standard" layout). */
export const BUTTON = { A: 0, B: 1, RT: 7, START: 9, UP: 12, DOWN: 13, LEFT: 14, RIGHT: 15 } as const;
/** Stick travel ignored around the centre, rescaled so the rest still reaches ±1. */
export const DEADZONE = 0.18;
/** Right-trigger travel that counts as a pull. */
const TRIGGER = 0.3;

/** The parts of a Gamepad this module reads. */
export interface PadLike {
  readonly mapping: string;
  readonly axes: readonly number[];
  readonly buttons: readonly { readonly pressed: boolean; readonly value: number }[];
}

/** A stick axis with the dead zone removed: 0 inside it, rescaled to ±1 outside. */
export function deadzone(v: number, dz = DEADZONE): number {
  const a = Math.abs(v);
  if (a <= dz) return 0;
  const s = (Math.min(1, a) - dz) / (1 - dz);
  return v < 0 ? -s : s;
}

const pressed = (p: PadLike, i: number): boolean => p.buttons[i]?.pressed ?? false;

/** A button that went down since the last poll. */
function edge(p: PadLike, prev: Uint8Array, i: number): boolean {
  const now = pressed(p, i);
  const was = prev[i] === 1;
  prev[i] = now ? 1 : 0;
  return now && !was;
}

/**
 * Reads one pad into `out` (adding to what earlier pads gave this frame) and reports button presses as commands.
 * `prev` holds this pad's buttons from the last poll (edges only fire once).
 */
export function readPad(p: PadLike, out: ControlSource, prev: Uint8Array, command: CommandSink, menu: boolean): void {
  const dpad = (pressed(p, BUTTON.RIGHT) ? 1 : 0) - (pressed(p, BUTTON.LEFT) ? 1 : 0);
  const dpadY = (pressed(p, BUTTON.UP) ? 1 : 0) - (pressed(p, BUTTON.DOWN) ? 1 : 0);
  out.steer += dpad + deadzone(p.axes[0] ?? 0);
  out.throttle += dpadY - deadzone(p.axes[1] ?? 0); // stick up is negative
  const trigger = (p.buttons[BUTTON.RT]?.value ?? 0) > TRIGGER || pressed(p, BUTTON.RT);
  out.fire = out.fire || pressed(p, BUTTON.A) || trigger;

  // Every button's edge is read each poll, so `prev` stays current.
  const a = edge(p, prev, BUTTON.A);
  const b = edge(p, prev, BUTTON.B);
  const start = edge(p, prev, BUTTON.START);
  const up = edge(p, prev, BUTTON.UP);
  const down = edge(p, prev, BUTTON.DOWN);
  if (start) command('menu');
  if (menu) {
    if (a) command('confirm');
    if (b) command('back');
    if (up) command('up');
    if (down) command('down');
  } else if (a) command('start');
}

export interface Gamepads {
  readonly source: ControlSource;
  menu: boolean;
  /** Polls the connected pads (call once a frame). Returns whether a pad gave any input. */
  poll(): boolean;
  dispose(): void;
}

export function createGamepads(target: Window, nav: Navigator, command: CommandSink): Gamepads {
  const source = createSource();
  const prev = new Map<number, Uint8Array>();
  let connected = 0;
  const on = (): void => void connected++;
  const off = (e: GamepadEvent): void => {
    connected = Math.max(0, connected - 1);
    prev.delete(e.gamepad.index);
    releaseSource(source);
  };
  target.addEventListener('gamepadconnected', on);
  target.addEventListener('gamepaddisconnected', off as EventListener);
  const pads: Gamepads = {
    source,
    menu: false,
    poll() {
      releaseSource(source);
      if (connected === 0 || typeof nav.getGamepads !== 'function') return false;
      const list = nav.getGamepads();
      let used = false;
      for (const p of list) {
        if (!p || !p.connected || p.mapping !== 'standard') continue;
        let pv = prev.get(p.index);
        if (!pv) prev.set(p.index, (pv = new Uint8Array(p.buttons.length)));
        readPad(p, source, pv, command, pads.menu);
        used = used || source.steer !== 0 || source.throttle !== 0 || source.fire;
      }
      if (source.steer > 1) source.steer = 1;
      if (source.steer < -1) source.steer = -1;
      if (source.throttle > 1) source.throttle = 1;
      if (source.throttle < -1) source.throttle = -1;
      return used;
    },
    dispose() {
      target.removeEventListener('gamepadconnected', on);
      target.removeEventListener('gamepaddisconnected', off as EventListener);
    },
  };
  return pads;
}
